//initial redux
import { put, call, takeEvery, fork, all, take, select, takeLatest } from "redux-saga/effects";
import * as router from "react-router-redux";
import { eventChannel, delay } from "redux-saga";
import reportsservice from "../../../services/reports";
import moment from "moment-timezone";
import pointsservice from "../../../services/points";
import {types as sitetreetypes} from "../../../services/SiteTree/redux";
import ticketservice from "../../../services/ticket";
import pointName from "../../../utils/pointName";
import unitsservice from "../../../utils/units";
import { DateTime } from "../../../utils";
import steppedSeries from "../../../utils/steppedSeries";

const LIVE_DATA_INTERVAL = 5000;

const initialState = {
    instances: {},
	isBusy: false,
	errors: [],
	hasError: false
};

/////////
//types
export const types = {
	START_LIVE_DATA: "components/livelinechart/START_LIVE_DATA",
    STOP_LIVE_DATA: "components/livelinechart/STOP_LIVE_DATA",
    UPDATE_LAST_UPDATED: "components/livelinechart/UPDATE_LAST_UPDATED",
};

function startLiveData(state, action){
    const id = action.payload.id;
    const chart = action.payload.chart;
    const rootView = action.payload.rootView;
    let liveLineData = {
        id, chart, rootView, lastUpdate: null,
    };
    return {...state, instances: {...state.instances, [id]: liveLineData}};
}

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
            case types.START_LIVE_DATA:{
                return startLiveData(state, action);
            }
            case types.STOP_LIVE_DATA:{
                return {...state, instances: {...state.instances, [action.payload.id]: undefined}};
            }
            case types.UPDATE_LAST_UPDATED:{
                return {...state, instances: {...state.instances, [action.payload.id]: {...state.instances[action.payload.id], lastUpdate: action.payload.lastUpdate}}}
            }
			default:
				return state;
		}
	}
};

function* updateLiveData(config, fromDate, toDate){
    if(config.view.name.toLowerCase().indexOf("ticket") !== -1){
        const currentSite = yield select(x => x.sitetree.currentSite);
        const ticketSales = yield call(ticketservice.GetLive, fromDate, toDate, currentSite.name);
        addDataToSeries(config.series, ticketSales);
    }else{
        const viewData = yield call(pointsservice.TrendableDataGetData, config.view.objectID, fromDate, toDate, undefined, undefined, undefined, true);
        addDataToSeries(config.series, viewData);
    }
}

function addDataToSeries(series, data){
    if(series && data && data.length){
        try {
            for(var ii = 0; ii < data.length; ii++){
                if(data[ii] && data[ii].value && data[ii].timestamp) {
                    if (typeof data[ii].timestamp === 'string') {
                        data[ii].timestamp = DateTime.getGMTDate(data[ii].timestamp);
                    }
                    else {
                        var now = new Date();
                        data[ii].timestamp = new Date(data[ii].timestamp.getTime());
                    }
                    if(series && data && data[ii] && data[ii].timestamp && data[ii].value) {
                        series.addPoint([data[ii].timestamp.getTime(), data[ii].value], false);
                    }
                }
            }
            
            if(series.chart) {
                series.chart.redraw();
            }
        }
        catch(exception) {
            // ignore anything that goes wrong in here as its probably a timing issue with HighCharts.
        }
    }
}

function addSeries(chart, view){
    let symbol = unitsservice.stringToSymbol(view.units);
    let isSteppedSeries = steppedSeries.isSteppedSeries(view.name, view.description);
    return chart.addSeries({
        type: 'line',
		  name: pointName.formatDescription(view),
		  step: isSteppedSeries ? "left" : undefined,
        yAxis: getAxisIndex(chart, symbol ? symbol : view.units),
        data: [],
        units: symbol || "",
        marker: {
			enabled: false,
		},
    });
}

//returns teh index of the y index with teh specified units.
//if it doesn't exist it creates a new axis with teh units and returns the index for that axis.
function getAxisIndex(chart, units){
	if(!chart.options){return;}
	let axisIndex = 0;
	//if the first yaxis doesnt have a value set then
	if(!chart.yAxis[0].userOptions.title){
		chart.yAxis[0].update({
			title: {
				text: units,
				align: "high",
				rotation: 0,
				margin: 0,
				y: -30,
				offset: 14,
				style: {
					"font-weight": "600",
					"font-size": "15px",
				},
			},
            labels: {
                style: {
                    fontSize: "14px",
                    color: '#999',
                }
            }
		});
	}else{
		//search for existing with same units and use that, else add a new axis.
		let axisFound = false;
		for(var ii = 0; ii < chart.yAxis.length; ii++){
			let wkAxis = chart.yAxis[ii];
			if(wkAxis.userOptions.title.text == units){
				axisFound = true;
				axisIndex = ii;
				break;
			}
		}
		if(!axisFound){
			axisIndex = chart.yAxis.length;
			chart.addAxis({
                opposite: chart.yAxis.length % 2 !== 0,
				title: {
					text: units,
					align: "high",
					rotation: 0,
                    margin: 0,
					y: -30,
					offset: 14,
					style: {
						"font-weight": "600",
						"font-size": "15px",
					},
				},
                labels: {
                    style: {
                        fontSize: "14px",
                        color: '#999',
                    }
                }
			});
		}
	}
	return axisIndex;
}

function* startLiveInterval(payload){
    let instance = yield select(x => x.livelinechart.instances[payload.id]);
    const flattenedSiteTree = yield select(x => x.sitetree.flattenedSiteTree);
    const currentSite = yield select(x => x.sitetree.currentSite);
    let children = [];
    if(instance.rootView && instance.rootView._tree.children && instance.rootView._tree.children.length){
        for(var ii = 0; ii < instance.rootView._tree.children.length; ii++){
            let wkChild = flattenedSiteTree[instance.rootView._tree.children[ii]];
            children.push({
                view: wkChild,
                series: addSeries(instance.chart, wkChild)
            });
        }
    }
    if(instance.rootView.name.indexOf("icket") !== -1){
        console.warn(instance.rootView);
    }
    instance.chart.update({
        title: {
            text: instance.rootView.name.replace(/^\d+\.\s*/, ''),
        }
    });
    while(instance && instance.chart){
        let utcOffset = 0;
        if(currentSite && currentSite.momentTimezone){
            utcOffset = moment().tz(currentSite.momentTimezone).utcOffset();
        }
        let fromDate = moment().utc().subtract(1, 'days').add(utcOffset, 'm');
        let chartFromDate = moment().subtract(1, 'days').add(utcOffset, 'm');
        if(instance.lastUpdate){
            fromDate = instance.lastUpdate;
        }
        let toDate = moment().utc().add(utcOffset, 'm');
        instance.chart.xAxis[0].setExtremes(chartFromDate.valueOf(), toDate.valueOf());
        yield all([children.map(child => call(updateLiveData, child, fromDate, toDate))]);
        yield put(actions.updateLastUpdated(instance.id, toDate));
        yield delay(LIVE_DATA_INTERVAL);
        instance = yield select(x => x.livelinechart.instances[payload.id]);
    }
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([
            this.onStartLiveData(),
            this.onSiteChange(),
        ]);
    },
    *onStartLiveData(){
        yield takeEvery(types.START_LIVE_DATA, function*({payload}){
            yield fork(startLiveInterval, payload);
        });
    },
    *onSiteChange() {
        yield takeEvery([sitetreetypes.SET_CURRENT_SITE, sitetreetypes.ON_LOAD], function*({payload}) {
            const onPage = yield select(x => x.live.onPage);
            if(!onPage){
                return;
            }
            const instances = yield select(x => x.livelinechart.instances);

            for (var instance in instances) {
                if (instances.hasOwnProperty(instance)) {
                    if(instances[instance].chart && instances[instance].chart.series && instances[instance].chart.series.length) {
                        instances[instance].chart.series.forEach(series => {
                            series.update({data: []});
                        });
                    }
                    yield put(actions.updateLastUpdated(instances[instance].id, undefined));
                }
            }
        });
    }
};

////////
//actions
export const actions = {
    startLiveData(id, rootView, chart) {
        return {
           type: types.START_LIVE_DATA,
           payload: {id, rootView, chart}
        };
     },
     stopLiveData(id) {
        return {
           type: types.STOP_LIVE_DATA,
           payload: {id}
        };
     },
     updateLastUpdated(id, lastUpdate){
         return {
             type: types.UPDATE_LAST_UPDATED,
             payload: {id, lastUpdate}
         };
     },
};



// WEBPACK FOOTER //
// ./src/components/LiveComponents/LiveLineChart/redux.js